import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { LabelEditor } from './wysiwyg/LabelEditor';
import type { Template as WysiwygTemplate } from './wysiwyg/types';
import { createLegacyAssetResolver } from './wysiwyg/legacyAssets';
import { normalizeLoadedTemplate } from './wysiwyg/templateNormalization';
import { AUTH_STORAGE_KEY, DEFAULT_API_BASE_URL } from './constants';
import type { BrandRecord, MarketRecord } from './types';

type ApiTemplate = {
  id: number;
  name: string;
  content: string;
  organizationId?: number;
  active?: boolean;
  isPublic?: boolean;
  marketIds: number[];
  brandIds?: number[];
};

type EditTemplatePageProps = {
  onClose: () => void;
  onSave: (
    template: WysiwygTemplate,
    originalTemplate: ApiTemplate,
    marketIds: number[],
    brandIds: number[],
  ) => Promise<void>;
  organizationId?: string;
  fallbackTemplate?: ApiTemplate | null;
  orgMarketIds?: number[];
  availableMarkets?: MarketRecord[];
  orgBrandIds?: number[];
  availableBrands?: BrandRecord[];
};

const isTemplateRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value);

export default function EditTemplatePage({
  onClose,
  onSave,
  organizationId,
  fallbackTemplate,
  orgMarketIds,
  availableMarkets,
  orgBrandIds,
  availableBrands,
}: EditTemplatePageProps) {
  const location = useLocation();

  // Prefer the parent-provided template from the refreshed templates list after save/reload.
  const apiTemplate: ApiTemplate | null = fallbackTemplate || location.state?.template || null;

  const initialTemplate = useMemo<WysiwygTemplate | null>(() => {
    if (!apiTemplate?.content) return null;
    try {
      const content = apiTemplate.content;
      let parsed: unknown = JSON.parse(content);
      while (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      if (!isTemplateRecord(parsed)) {
        return null;
      }

      const template: WysiwygTemplate = {
        ...parsed,
        // Preserve invalid dimensions as NaN for the editor's existing
        // dimension normalization; do not invent physical label dimensions here.
        width: typeof parsed.width === 'number' ? parsed.width : Number(parsed.width),
        height: typeof parsed.height === 'number' ? parsed.height : Number(parsed.height),
        background: typeof parsed.background === 'string' ? parsed.background : '#ffffff',
        // Malformed/legacy content may not contain an elements array at all.
        elements: Array.isArray(parsed?.elements) ? parsed.elements : [],
        id: String(apiTemplate.id),
        name: apiTemplate.name,
      };

      // Defensively sanitize element properties so malformed/legacy/migrated
      // template data cannot crash rendering with an unhandled exception.
      return normalizeLoadedTemplate(template);
    } catch (e) {
      console.error('Failed to parse template content:', e);
      return null;
    }
  }, [apiTemplate]);

  const legacyAssetResolver = useMemo(() => {
    if (!organizationId) {
      return undefined;
    }
    return createLegacyAssetResolver({
      organizationId,
      apiBaseUrl: DEFAULT_API_BASE_URL,
      getToken: () => window.localStorage.getItem(AUTH_STORAGE_KEY),
    });
  }, [organizationId]);

  const handleSave = (template: WysiwygTemplate, marketIds: number[], brandIds: number[]) => {
    if (!apiTemplate) return;
    onSave(template, apiTemplate, marketIds, brandIds);
  };

  if (!apiTemplate) {
    return (
      <div className="stack" data-testid="edit-template-page">
        <PageHeader
          title="Edit Template"
          subtitle="Templates"
          actions={
            <button
              className="button secondary"
              onClick={onClose}
              data-testid="edit-template-close"
            >
              Back to Templates
            </button>
          }
        />
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>Template not found. Please go back and try again.</p>
        </div>
      </div>
    );
  }

  if (!initialTemplate) {
    return (
      <div className="stack" data-testid="edit-template-page">
        <PageHeader
          title={`Edit: ${apiTemplate.name}`}
          subtitle="Templates"
          actions={
            <button
              className="button secondary"
              onClick={onClose}
              data-testid="edit-template-close"
            >
              Back to Templates
            </button>
          }
        />
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>Failed to load template content. The template data may be corrupted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stack" data-testid="edit-template-page">
      <PageHeader
        title={`Edit: ${apiTemplate.name}`}
        subtitle="Templates"
        actions={
          <button className="button secondary" onClick={onClose} data-testid="edit-template-close">
            Back to Templates
          </button>
        }
      />

      <div style={{ marginTop: -12 }}>
        <LabelEditor
          key={`${organizationId}:${apiTemplate.id}`}
          initialTemplate={initialTemplate}
          onSave={handleSave}
          legacyAssetResolver={legacyAssetResolver}
          organizationId={organizationId ? Number(organizationId) : undefined}
          marketIds={Array.isArray(apiTemplate.marketIds) ? apiTemplate.marketIds : orgMarketIds}
          availableMarkets={availableMarkets}
          brandIds={initialTemplate.allBrands
            ? availableBrands?.map((brand) => brand.id)
            : apiTemplate?.brandIds?.length ? apiTemplate.brandIds : initialTemplate.brandIds || orgBrandIds}
          availableBrands={availableBrands}
        />
      </div>
    </div>
  );
}
